import { useState } from "react";
import { useAppData } from "../../lib/AppDataContext";
import { Card } from "../../components/shared/Card";
import { Modal } from "../../components/shared/Modal";
import { toast } from "../../components/shared/Toast";

function slugify(t) { return String(t || "").toLowerCase().trim().replace(/[^a-z0-9\s-]/g, "").replace(/\s+/g, "-"); }

export function CitiesView() {
  const { cities, mastersLoading, addCity, updateCity, toggleCityActive, deleteCity } = useAppData();
  const [adding, setAdding] = useState(false);
  const [editing, setEditing] = useState(null);
  const [deleting, setDeleting] = useState(null);

  if (mastersLoading) {
    return <div className="p-6"><Card><div className="p-10 text-center text-slate-500 text-sm">Loading…</div></Card></div>;
  }

  return (
    <div className="p-4 sm:p-6 space-y-4">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="text-xl font-bold text-slate-900">Cities</h1>
          <p className="text-xs text-slate-500 mt-0.5">{cities.length} total · {cities.filter((c) => c.active).length} active</p>
        </div>
        <button onClick={() => setAdding(true)} className="shrink-0 whitespace-nowrap px-4 py-2 bg-navy text-white rounded text-sm font-medium hover:bg-navy/90">+ Add city</button>
      </div>

      <Card className="overflow-hidden">
        <div className="overflow-x-auto"><table className="w-full text-sm min-w-[560px]">
          <thead className="bg-slate-50 border-b border-slate-200">
            <tr className="text-left text-xs uppercase tracking-wider text-slate-500">
              <th className="px-4 py-2 font-semibold">Name</th>
              <th className="px-4 py-2 font-semibold">Slug</th>
              <th className="px-4 py-2 font-semibold">State</th>
              <th className="px-4 py-2 font-semibold text-center">Localities</th>
              <th className="px-4 py-2 font-semibold">Status</th>
              <th className="px-4 py-2 font-semibold text-right"></th>
            </tr>
          </thead>
          <tbody>
            {cities.length === 0 && <tr><td colSpan={6} className="px-4 py-10 text-center text-slate-400">No cities yet.</td></tr>}
            {cities.map((c) => (
              <tr key={c.id} className="border-b border-slate-100 hover:bg-slate-50">
                <td className="px-4 py-3 font-medium text-slate-900">{c.name}</td>
                <td className="px-4 py-3 text-slate-600 text-xs">/{c.slug}</td>
                <td className="px-4 py-3 text-slate-700">{c.state}</td>
                <td className="px-4 py-3 text-center text-slate-700">{c._count?.localities ?? 0}</td>
                <td className="px-4 py-3">
                  <span className={`inline-block px-2 py-0.5 rounded border text-xs ${c.active ? "bg-emerald-50 text-emerald-700 border-emerald-200" : "bg-slate-100 text-slate-600 border-slate-200"}`}>
                    {c.active ? "Active" : "Inactive"}
                  </span>
                </td>
                <td className="px-4 py-3 text-right space-x-1 whitespace-nowrap">
                  <button onClick={() => toggleCityActive(c.id).catch((e) => toast(e?.message || "Failed"))} className="text-xs text-slate-600 hover:underline">{c.active ? "Deactivate" : "Activate"}</button>
                  <span className="text-slate-300">·</span>
                  <button onClick={() => setEditing(c)} className="text-xs text-navy hover:underline">Edit</button>
                  <span className="text-slate-300">·</span>
                  <button onClick={() => setDeleting(c)} className="text-xs text-red-600 hover:underline">Delete</button>
                </td>
              </tr>
            ))}
          </tbody>
        </table></div>
      </Card>

      {(adding || editing) && (
        <CityFormModal
          key={editing ? editing.id : "new"}
          initial={editing}
          onClose={() => { setAdding(false); setEditing(null); }}
          onSave={async (data) => {
            try {
              if (editing) { await updateCity(editing.id, data); toast("City updated"); }
              else { await addCity(data); toast("City created"); }
              setAdding(false); setEditing(null);
            } catch (err) { toast(err?.message || "Failed"); }
          }}
        />
      )}
      {deleting && (
        <Modal onClose={() => setDeleting(null)} title="Delete city?" size="sm">
          <div className="p-4 space-y-3 text-sm">
            <p>Soft-delete <strong>{deleting.name}</strong>?</p>
            <p className="text-xs text-amber-700">Only allowed if no active localities depend on this city.</p>
            <div className="flex justify-end gap-2 pt-2">
              <button onClick={() => setDeleting(null)} className="px-3 py-1.5 text-xs border border-slate-300 rounded hover:bg-slate-50">Cancel</button>
              <button onClick={async () => {
                try { await deleteCity(deleting.id); toast("City deleted"); setDeleting(null); }
                catch (err) { toast(err?.message || "Failed"); }
              }} className="px-3 py-1.5 text-xs bg-red-600 text-white rounded hover:bg-red-700">Delete</button>
            </div>
          </div>
        </Modal>
      )}
    </div>
  );
}

function CityFormModal({ initial, onClose, onSave }) {
  const [form, setForm] = useState(initial || { name: "", slug: "", state: "Maharashtra", active: true, sortOrder: 0 });
  const [saving, setSaving] = useState(false);

  return (
    <Modal onClose={onClose} title={initial ? "Edit city" : "Add city"} size="sm" dismissOnBackdrop={false}>
      <form onSubmit={async (e) => {
        e.preventDefault();
        if (!form.name || !form.slug || !form.state) return;
        setSaving(true);
        try { await onSave(form); } finally { setSaving(false); }
      }} className="p-4 space-y-3 text-sm min-h-0 overflow-y-auto">
        <div>
          <label className="block text-xs font-medium text-slate-600 mb-1">Name *</label>
          <input value={form.name} onChange={(e) => setForm((f) => ({ ...f, name: e.target.value, slug: f.slug || slugify(e.target.value) }))} className="w-full px-2 py-1.5 border border-slate-300 rounded text-sm" required />
        </div>
        <div>
          <label className="block text-xs font-medium text-slate-600 mb-1">Slug *</label>
          <input value={form.slug} onChange={(e) => setForm((f) => ({ ...f, slug: slugify(e.target.value) }))} className="w-full px-2 py-1.5 border border-slate-300 rounded text-sm" required />
        </div>
        <div>
          <label className="block text-xs font-medium text-slate-600 mb-1">State *</label>
          <input value={form.state} onChange={(e) => setForm((f) => ({ ...f, state: e.target.value }))} className="w-full px-2 py-1.5 border border-slate-300 rounded text-sm" required />
        </div>
        <div className="flex justify-end gap-2 pt-2">
          <button type="button" onClick={onClose} className="px-3 py-1.5 text-xs border border-slate-300 rounded hover:bg-slate-50">Cancel</button>
          <button type="submit" disabled={saving} className="px-4 py-1.5 text-xs bg-navy text-white rounded hover:bg-navy/90 disabled:opacity-50">{saving ? "Saving…" : "Save"}</button>
        </div>
      </form>
    </Modal>
  );
}
